import * as THREE from 'three';

export default class ShaderCanvas {
  constructor() {
    this.setting();
    this.initScene();
    this.initCamera();
    this.initRenderer();
    this.initShaders();
    this.initMesh();
    this.initResizeHandler();
    this.animate();
  }

  setting() {
    this.canvasSize = {
      w: window.innerWidth,
      h: window.innerHeight,
    };
  }

  initScene() {
    this.scene = new THREE.Scene();
  }

  initCamera() {
    this.fov    = 60;
    this.fovRad = (this.fov / 2) * (Math.PI / 180);
    this.dist   = this.canvasSize.h / 2 / Math.tan(this.fovRad);
    this.camera = new THREE.PerspectiveCamera(
      this.fov,
      this.canvasSize.w / this.canvasSize.h,
      0.1,
      2000
    );
    this.camera.position.z = this.dist;
  }

  initRenderer() {
    this.renderer   = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
    });
    this.renderer.setPixelRatio(window.devicePixelRatio);
    this.renderer.setSize(this.canvasSize.w, this.canvasSize.h);
    this.renderer.setClearColor(0x000000, 0);
    document.body.prepend(this.renderer.domElement);
  }

  initShaders() {
    this.vertexShader = `
      #ifdef GL_ES
      precision mediump float;
      #endif

      varying vec2 vUv;
      uniform float time;
      varying vec2 surfacePosition;

      void main() {
        vUv = uv;
        
        // 波のような変動を加えたUV座標
        surfacePosition = vUv + vec2(sin(vUv.y * 10.0 + time * 5.0) * 0.1, 1.0); 
        
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    this.fragmentShader = `
      #ifdef GL_ES
      precision mediump float;
      #endif

      uniform float time;
      varying vec2 surfacePosition;

      #define MAX_ITER 4
      void main(void) {
        vec2 sp = clamp(surfacePosition, 0.0, 1.0); // 波の変動が反映されたUV座標
        vec2 p  = sp * 5.0 - vec2(10.0);
        vec2 i  = p;
        float c = 1.0;

        float inten = 0.04;

        for (int n = 0; n < MAX_ITER; n++) {
          float t = time * (1.0 - (10.0 / float(n + 1))); // 時間に依存した変数を計算する
          i = p + vec2(cos(t - i.x) + sin(t + i.y), sin(t - i.y) + cos(t + i.x)); // 波の座標を更新する
          c += 0.7 / length(vec2(p.x / (sin(i.x + t) / inten), p.y / (cos(i.y + t) / inten))); // 波の強度を計算する
        }
        c /= float(MAX_ITER); // MAX_ITERで割って平均を取る
        c = 1.0 - sqrt(c); // 平均を元に最終的な波の強度を決定する

        vec3 color = vec3(c * c); // 基本の白色
        // 色収差の計算
        float aberrationStrength = 0.04; // 色収差の強度を微調整
        vec3 r = vec3(color.r + aberrationStrength, 0.0, 0.0); // 赤色の収差
        vec3 g = vec3(0.0, color.g - aberrationStrength, 0.0); // 緑色の収差
        vec3 b = vec3(0.0, 0.0, color.b + aberrationStrength); // 青色の収差

        // 動的な透明度の変化
        float alpha = smoothstep(0.8, 1.0, c);
        vec3 finalColor = mix(vec3(r.r, g.g, b.b), color, 0.5);
        
        // 最終
        gl_FragColor = vec4(finalColor, alpha);
      }
    `;
  }

  initMesh() {
    const geometry = new THREE.PlaneGeometry(this.canvasSize.w, this.canvasSize.h);
    this.material = new THREE.ShaderMaterial({
      uniforms: {
        surfacePosition: { value: 0.0 },
        time: { value: 0.0 },
      },
      vertexShader: this.vertexShader,
      fragmentShader: this.fragmentShader
    });
    this.mesh = new THREE.Mesh(geometry, this.material);
    this.scene.add(this.mesh);
  }

  initResizeHandler() {
    window.addEventListener('resize', () => {
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(window.innerWidth, window.innerHeight);
    });
  }

  animate() {
    requestAnimationFrame(() => this.animate());
    this.material.uniforms.time.value += 0.001;
    this.renderer.render(this.scene, this.camera);
  }
}
